/* eslint-disable @typescript-eslint/member-ordering */

import type {
  DETERMINE_PAYMENT_METHOD_RETURN_TYPE,
  FEE_STRUCTURE,
  PAYMENT_GATEWAY_NAME,
  PLAN_TYPE,
} from 'constants/payment';

export enum ModalType {
  showModel = 1,
}

export type ExtraItem = {
  amount: string | number;
  amountError?: boolean;
  createdAt?: string;
  description: string;
  descriptionError?: boolean;
  dueDate: Date | string;
  invoiceID?: string | null;
  isInstallment?: number;
  paidAmount?: number;
  paymentPlanID?: string;
  paymentScheduleID?: string;
  salesTax?: number;
  status?: string;
  taxAmount?: null | number;
  totalAmount?: number;
  quantity?: number;
  isTaxable?: number;
};

export type InstallmentSchedule = {
  amount: string;
  description: string;
  dueDate: Date;
  error?: boolean;
  invoiceID?: string;
  invoiceFee?: number;
  paymentScheduleID?: string;
  status?: string;
  paidAmount?: number;
  totalAmount?: number;
};

export interface PaymentItem {
  amount: number;
  createdAt: string; // Assuming this is a date string
  description: string;
  dueDate: string; // Assuming this is a date string
  invoiceID: string;
  invoiceFee?: number;
  isInstallment?: number;
  paidAmount: number;
  paymentScheduleID: string;
  status: string;
  taxAmount: number | null;
  totalAmount: number;
  updatedAt: string; // Assuming this is a date string
  quantity?: number;
  isDownPayment?: boolean;
}

export enum OverPaymentAllocation {
  NEXT = 'NEXT',
  LAST = 'LAST',
}

export interface SchoolPaymentProcessor {
  displayName: string;
  fees: PlanFee[];
  isActive: boolean;
  processor: string;
  schoolPaymentProcessorID: string;
}

export interface PaymentProcessingFee {
  flatFee: number;
  rate: number;
}

export interface SchoolPaymentConfig {
  achFee: PaymentProcessingFee;
  allowedMissedPayment: number;
  basePaymentUrl: string;
  cardknoxMid?: number;
  ccFee: PaymentProcessingFee;
  city: string;
  contactEmail: string | null;
  contactPhone: string | null;
  country: string;
  dbaName?: string;
  feeStructure?: FEE_STRUCTURE;

  gracePeriod: number;
  isAnthologyPaymentEnabled: boolean;
  plan?: PLAN_TYPE;
  processorMid?: string;
  SchoolPaymentProcessors?: SchoolPaymentProcessor[];
  schoolID: string;
  state: string;
  status: string;
  streetAddress: string;
  transactionFeeManagement: string;
  zip: string;
  nonSufficientFundFee: number;
  enableNonSufficientFundFee: boolean;
  cancellationFee: number;
  enableCancellationFee: boolean;
  declinedTransactionFee: number;
  enableDeclinedTransactionFee: boolean;
  enableAutoCancelPaymentOnWithdrawal: boolean;
  requireSequentialPayment: boolean;
  sequentialPaymentOrder?: string[];
  enableOverPayment: boolean;
  enableMinPartialPayment: boolean;
  overPaymentAllocation: 'NEXT' | 'LAST';
  enablePartialPayment: boolean;
  minPartialPaymentRequired: number;
  enableLateFee: boolean;
  lateFee: number;
  enableTaxRate: boolean;
  taxRate: number;
  allowedPaymentMethods?: string[];
  isPlanActive?: boolean;
  paymentGatewayName: string;
  publicIntegrationKey?: string | null;
  stripeDashboard?: 'full' | 'express' | 'none';
}

export interface AutoPaySchedule {
  amount: number;
  description: string;
  dueDate: string;
}

export interface PaymentSummary {
  amount: number;
  autoPay: number;
  createdAt: string; // Assuming this is a date string
  currentBalance: string;
  currentDue: number;
  pastDue: number;
  duePaymentDate: string;
  enrolledBy: number;
  excessAmount: number;
  isActive: number;
  isCustom: number;
  isAnthology?: number;
  lastPaymentDate: string | null;
  maturityDate: string;
  name: string;
  nextPaymentDate: string | null;
  paidAmount: number;
  paymentProgress: number;
  paymentPlanID: string;
  PaymentSchedules: PaymentItem[];
  prerequisitePaymentPlanName?: string;
  paymentPlanStatus: string;
  paymentStartDate?: string;
  Program: {
    name: string;
    ProgramID: number;
  };
  School: {
    name: string;
    schoolID: string;
  };
  SchoolPaymentConfig: SchoolPaymentConfig;
  autoPaySchedule?: AutoPaySchedule[];
  status: string;
  tax: number;
  taxAmount: number | null;
  totalAmount: number;
  remainingAmountToBePaid: number;
  totalInstallmentAmount: number;
  updatedAt: string; // Assuming this is a date string
  user: {
    avatar: string;
    email: string;
    firstName: string;
    issuedID: string;
    lastName: string;
  };
  userID: number;
}

export interface PaymentSummaryWithUser {
  amount: number;
  autoPay: number;
  createdAt: string; // Assuming this is a date string
  currentBalance: string;
  currentDue: number;
  duePaymentDate: string;
  enrolledBy: number;
  excessAmount: number;
  isActive: number;
  lastPaymentDate: string | null;
  maturityDate: string;
  nextPaymentDate: string | null;
  paidAmount: number;
  paymentPlanID: string;
  PaymentSchedules: PaymentItem[];
  paymentStartDate?: string;
  Program: {
    name: string;
    ProgramID: number;
  };
  School: {
    name: string;
    schoolID: string;
  };
  SchoolPaymentConfig: SchoolPaymentConfig;
  status: string;
  tax: number;
  taxAmount: number | null;
  totalAmount: number;
  totalInstallmentAmount: number;
  updatedAt: string; // Assuming this is a date string
  user: {
    avatar: string;
    email: string;
    firstName: string;
    issuedID: string;
    lastName: string;
  };
  userID: number;
}

export interface PaymentProgressProps {
  maturityDate: string;
  paidToDate: string;
  paymentSchedule: string;
  percentage: string;
}

export interface PaymentAmountDetailsProps {
  autoPay?: boolean;
  isCancelledPlan?: boolean;
  paymentData: PaymentSummary;
  paymentSchedule?: string;
  showEditButton?: boolean;
}

export interface SchoolPaymentConfigProps {
  schoolPaymentData: SchoolPaymentConfig;
}

export interface PaymentPlanItem {
  amount: number;
  description: string;
  dueDate: string;
  isInstallment?: boolean | number;
  taxAmount?: number | null;
  totalAmount: number;
  quantity?: number;
  isDownPayment?: boolean;
  isTaxable?: number;
  invoiceFee?: number;
  paidAmount?: number;
  paymentScheduleID?: string;
  status?: string;
}

export interface PaymentPlanAPIRequest {
  amount: number;
  items: PaymentPlanItem[];
  name?: string;
  programID: number;
  schoolID: string;
  tax?: number;
  taxAmount?: number;
  totalAmount: number;
  totalInstallmentAmount: number;
}

export interface PaymentPlanAPIRequestParams {
  userID: string;
}

export interface PaymentPlanUpdateRequest {
  amount: number;
  paymentSchedules?: PaymentPlanItem[];
  tax?: number;
  taxAmount?: number;
  totalAmount: number;
  totalInstallmentAmount: number;
  name?: string;
}

export interface PaymentPlanUpdateRequestParams {
  paymentPlanID: string;
  userID: string;
}

export interface PaymentPlanCancelRequest {
  isActive: boolean;
}

export interface PaymentPlanCancelRequestParams {
  paymentPlanID: string;
  userID: string;
}

export interface PaymentMethodCreateRequest {
  accountType?: string;
  achToken?: string;
  bankName?: string;
  cardToken?: string;
  cvvToken?: string;
  exp?: string;
  googlePayToken?: string;
  name?: string;
  redirectTo?: string;
  routing?: string;
  tokenOnly?: boolean;
  tokenType: string;
}

export interface DefaultPaymentMethodRequest {
  isDefault: boolean;
}

export interface DefaultPaymentMethodRequestParams {
  paymentMethodID: string;
  userID: string;
}

export interface autoPayRequest {
  autoPay: boolean;
}

export interface autoPayRequestParams {
  paymentPlanID: string;
  userID: string;
}

export interface PaymentMethodForm {
  expiry: string;
  name: string;
  routingNumber: string;
}

export interface PaymentMethod {
  accountType: string;
  cardExpiry: string;
  cardType: string;
  createdAt: string;
  isDefault: number;
  maskedNumber: string;
  method: string;
  name: string;
  paymentMethodMappingID: string;
  updatedAt: string;
  userID: number;
}

export interface DeterminePaymentMethodTypeResponse {
  data: {
    paymentGatewayName:
      | PAYMENT_GATEWAY_NAME.CARDKNOX
      | PAYMENT_GATEWAY_NAME.IXOPAY;
    paymentMethodMappingID?: string;
    publicIntegrationKey?: string | null;
    redirectUrl?: string;
    returnType?: DETERMINE_PAYMENT_METHOD_RETURN_TYPE.REDIRECT;
  };
}

export interface PaymentMethodStatusResponse {
  data: {
    paymentMethodID?: string;
  };
}

/**
 * Response data shape returned by the charge POST and pay-invoice POST APIs.
 * Under IxoPay, a saved-method charge returns `returnType: FINISHED`
 * synchronously with a `transactionID` to poll (same as Cardknox); HPP returns
 * a `redirectUrl`. All four fields are optional.
 */
export interface ChargeResponseData {
  redirectUrl?: string;
  returnType?: DETERMINE_PAYMENT_METHOD_RETURN_TYPE;
  status?: string;
  transactionID?: string;
}

export interface ChargeAPIRequest {
  amount: number;
  paymentMethodID?: string;
  paymentPlanID: string;
  paymentToken?: string;
  paymentType?: string;
  reference?: string;
  redirectTo?: string;
  setAsDefault?: boolean;
  userID: number;
  accountType?: string;
}

export interface makePaymentRequest {
  amount: number;
  paymentMethodID?: string;
  paymentPlanID: string;
  paymentToken?: string;
  paymentType?: string;
  redirectTo?: string;
  setAsDefault?: boolean;
  userID: number;
}

export interface BankInfo {
  AccountNumber: string;
  AccountType: string;
  BankName: string;
  ConfirmAccountNumber: string;
  RoutingNumber: string;
}

export interface AddressInfo {
  city: string;
  state: string;
  streetAddress: string;
  Zip: string;
}

export interface SignerInfo {
  Address: AddressInfo;
  DateOfBirth: Date;
  FirstName: string;
  LastName: string;
  OwnerCellPhone: string;
  OwnershipPercentage: string;
  SSN: string;
  title: string;
}

export interface PaymentRegistrationForm {
  BankingInformation: BankInfo;
  BusinessAddress: AddressInfo;
  businessEmail: string;
  BusinessPhone: string;
  BusinessStartDate: Date;
  corporateName: string;
  dbaName: string;
  MailingAddress: AddressInfo;
  ownershipType: string;
  productSold: string;
  studentContactEmail: string;
  SignerInformationList: SignerInfo[];
  TaxId: string;
  website: string;
}

export enum MerchantApplicationStatus {
  Approved = 'APPROVED',
  Failed = 'FAILED',
  NoPermission = 'NOPERMISSION',
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Submitted = 'SUBMITTED',
}

export interface MerchantApplicationErrorData {
  code: number | string;
  message: string;
  messages: string[];
  shortname?: string;
}

export interface MerchantApplicationStatusResponse {
  status: MerchantApplicationStatus;
  submitData: Record<string, unknown> | null;
  errorData: MerchantApplicationErrorData | null;
  tosCompletedAt: string | null;
  requirementsDue?: boolean;
}

export interface PlanFee {
  label: string;
  rate: string;
  flatFee: string | null;
}

export interface SchoolPlan {
  planType: PLAN_TYPE;
  features: string[];
  isPopular: boolean;
  monthlyFee: number | null;
  fees: PlanFee[];
}

export interface InitiateAgreementRequest {
  feeStructure?: FEE_STRUCTURE;
  planType: PLAN_TYPE.PLUS | PLAN_TYPE.STANDARD | PLAN_TYPE.CUSTOM;
  schoolID: string;
}

export interface InitiateAgreementResponse {
  documentId: string;
  sessionId: string;
  embedUrl: string;
}

export interface SchoolOnboardingPlanSelection {
  agreementDocumentId: string;
  feeStructure?: FEE_STRUCTURE;
  plan: PLAN_TYPE.PLUS | PLAN_TYPE.STANDARD | PLAN_TYPE.CUSTOM;
  planIncludes: string[];
}

export interface StripeMerchantAddress {
  city: string;
  state: string;
  streetAddress: string;
  zip: string;
}

export interface CreateStripeMerchantRequest {
  businessAddress: StripeMerchantAddress;
  businessEmail: string;
  businessPhone?: string;
  dbaName: string;
  feeStructure?: FEE_STRUCTURE;
  plan: PLAN_TYPE.PLUS | PLAN_TYPE.STANDARD | PLAN_TYPE.CUSTOM;
  planIncludes: string[];
  schoolID: string;
  studentContactEmail: string;
  website: string;
}

export interface StripeBusinessBasicsFormValues {
  businessAddress: StripeMerchantAddress;
  businessEmail: string;
  businessPhone: string;
  dbaName: string;
  studentContactEmail: string;
  website: string;
}

export interface SendReminderRequest {
  paymentPlanID: string;
}

export type StripeAccountSessionComponent = 'disputes_list';

export interface SendReminderRequestParams {
  userID: string;
}

export interface ReportsAPIRequest {
  email: string;
  endDate?: string;
  fileType: string;
  schoolIDs: string[];
  startDate?: string;
  type: string;
}

export interface RefundTransactionAPIRequest {
  tokenType: string;
}

export interface PaymentMethodCreateRequestParams {
  userID: string;
}

export interface deletePaymentMethodRequestParams {
  paymentMethodID: string;
  userID: string;
}

export interface chargeInvoiceRequestParams {
  invoiceID: string;
}

export interface chargeInvoiceRequest {
  paymentMethodID?: string;
  paymentToken?: string;
}

export interface disablePaymentPlanRequest {
  programID: number;
  userIDs: number[];
}

export interface userActivePaymentPlansRequest {
  userIDs: string;
  programID: number;
}

export interface PaymentDetailsState {
  amount: number;
  type: string;
}

export interface CustomAmountErrorState {
  error: boolean;
  message: string;
}

export interface PaymentPlan {
  userID: number;
  programID: number;
  amount: number;
  taxAmount: number;
  tax: number;
  totalInstallmentAmount: number;
  totalAmount: number;
  isCustom?: boolean;
  items: PaymentPlanItem[];
}

export interface PaymentScheduleAPIRequest {
  templateID: string;
  paymentPlans: PaymentPlan[];
}

export interface DailySummaryUpdateAPIRequest {
  isEnabledDailyTransactionSummary: number;
  dailyTransactionSummarySchoolIDs: string[];
}

export interface DailySummaryUpdateAPIResponse {
  isEnabledDailyTransactionSummary: number;
  dailyTransactionSummarySchoolID: string[];
  userID: string;
}

export type ValueFormat = 'id' | 'encoded';

interface PaymentMethodOption {
  label: string;
  value: string;
}

export interface BuildExistingPaymentMethodOptionsResult {
  allowedMethods: string[];
  canAddNewMethod: boolean;
  existingMethodOptions: PaymentMethodOption[];
}
